// This page requires a stripe context and stripe.js which are included in
// the billing.(html/js) entrypoint, and not the main index.(html/js)

import React, { Component, Fragment, PureComponent } from 'react';
import moment from 'moment';
import _ from 'lodash';
import {
  Alert, Button, Divider, Spin, message, Form,
} from 'antd';
import { ElementsConsumer, CardElement } from '@stripe/react-stripe-js';

import { withCurrentUser } from '../../../whoami';
import { withAxios } from '../../../api/axios';

import { BusinessInfoForm } from './BusinessInfoForm';
import { SubscriptionForm, formatPrice } from './SubscriptionForm';
import { CardForm } from './CardForm';
import { withContentLayout } from '../../layout/Layout';
import { connectForm } from '../../form';
import { ButtonBar } from '../../elements';
import { ErrorsList } from '../../ErrorsList';


@withContentLayout
@withAxios
class BillingPage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      plans: null,
      info: null,
      plan: null,
      card: null,
      managed: null,
      loaded: false,
      expiryManagementAddOn: false,
    };
  }

  axiosRequest = async r => {
    // 2xx == OK
    // 3xx shouldn't happen
    // 401 -> reauth
    // 4xx -> alert and throw, but can be overridden
    // 5xx -> alert and throw
    try {
      const res = await this.props.axios.request(r);
      return res;
    } catch (e) {
      if (e.response.status === 401) {
        // Let the main app handle it
        document.location.href = '/';
        return null;
      }
      console.error('Internal', e);
      message.error('An error occurred. Please try again later');
      throw e;
    }
  }

  componentDidMount(props) {
    this.axiosRequest({
      url: '/api/billing/stripe/plans',
    }).then(({ data }) => {
      this.setState({ plans: data });
    }).catch(e => {
      console.error(e);
      message.error('An error occurred. Please try again later');
    });

    this.axiosRequest({
      url: '/api/billing/stripe/plans/expiry',
    }).then(({ data }) => {
      this.setState({ expiryManagementPlans: data });
    }).catch(e => {
      console.error(e);
      message.error('An error occurred. Please try again later');
    });

    this.axiosRequest({
      url: '/api/billing/stripe/organizations/me',
    }).then(({ data }) => {
      this.setState({ ...data, loaded: true });
    }).catch(e => {
      const msg = _.get(e, ['response', 'data', '_errors', '_request'], ['Billing is not currently available']).join(' ');
      this.setState({ error: msg });
    });
  }

  doUpdateRequest = async req => {
    try {
      const res = await this.axiosRequest({
        ...req,
        // 2xx and 4xx except 401
        validateStatus: s => (s < 300 || s === 400 || (s < 500 && s > 401)),
      });
      if (res.status >= 400) {
        return {
          success: false,
          data: res.data,
        };
      }
      this.setState(res.data);

      return {
        success: true,
        data: res.data,
      };
    } catch (e) {
      // Already caught earlier
      return {
        success: false,
      };
    }
  }

  doUpdate = data => this.doUpdateRequest({
    url: '/api/billing/stripe/organizations/me',
    method: this.state.managed ? 'patch' : 'post',
    data,
  })

  actions = {
    submitInfo: info => this.doUpdate({ ...info }),
    submitPlan: plan => this.doUpdate({ ...plan }),
    submitPayment: paymentMethod => this.doUpdate({ paymentMethod }),
    submitAllData: data => this.doUpdate({ ...data }),
  }

  render() {
    const {
      error, plans, expiryManagementPlans, plan, info, card, subscription, stripeInvoice, xeroInvoice, managed, organizationHints, loaded,
    } = this.state;

    if (error) {
      return (<div>
        <Alert message={error} type="error" />
      </div>);
    }
    if (!loaded || !plans) {
      return (<Spin style={{ width: '100%' }}/>);
    }

    if (managed
      && (card || stripeInvoice || xeroInvoice)
      && plan) {
      return (
        <SummaryPage
          plans={plans}
          expiryManagementPlans={expiryManagementPlans}
          expiryManagementAddOn={plan.expiryManagementAddon}
          info={info}
          card={card}
          subscription={subscription}
          stripeInvoice={stripeInvoice}
          xeroInvoice={xeroInvoice}
          plan={plan}
          actions={this.actions}
        />
      );
    }

    return (

      <ElementsConsumer>
        {({ stripe, elements }) => (
          <ActivatePage
            plans={plans}
            expiryManagementPlans={expiryManagementPlans}
            info={info}
            managed={managed}
            organizationHints={organizationHints}
            card={card}
            subscription={subscription}
            stripeInvoice={stripeInvoice}
            xeroInvoice={xeroInvoice}
            plan={plan}
            actions={this.actions}
            stripe={stripe} elements={elements}
          />
        )}
      </ElementsConsumer>
    );
  }
}

const Address = ({ address }) => {
  // If address exists and has a truthy (non empty) property
  if (address && Object.values(address).find(v => v)) {
    return (
      <div>
        {address.street}<br/>
        {address.street2}{address.street2 ? (<br/>) : null}
        {address.suburb} {address.postcode} {address.state}
      </div>
    );
  }

  return <div>Not Provided</div>;
};

class EditToggle extends Component {
  state = {
    editing: false,
  }

  startEdit = () => {
    this.setState({ editing: true });
  }

  stopEdit = () => {
    this.setState({ editing: false });
  }

  render() {
    const { editing } = this.state;
    const { editText = 'Edit', FormComponent, ...rest } = this.props;

    if (editing) {
      return (<FormComponent
        onSuccess={this.stopEdit}
        onCancel={this.stopEdit}
        {...rest}
              />);
    }
    return (<div style={{ position: 'relative' }}>
      { this.props.children }
      <Button style={{ position: 'absolute', bottom: 0, right: 0 }} type='primary' onClick={this.startEdit}>{editText}</Button>
    </div>);
  }
}

class DataTable extends Component {
  render() {
    const { data } = this.props;
    return (
      <table>
        <tbody>
          {data.map(({ label, data }, idx) => (
            <tr key={idx}>
              <th style={{ paddingRight: 10, verticalAlign: 'top' }}>{label}:</th>
              <td span={17}>{data}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}

const chargedOn = ({ anchor, billingPeriod }) => {
  if (!anchor || !billingPeriod) return '';
  const anchorMoment = moment(anchor);
  if (billingPeriod === 'yearly') {
    return `${anchorMoment.format('MMMM Do')} each year`;
  }
  return `${anchorMoment.format('Do')} day of the month`;
};

const getCurrentPaymentOption = paymentMethod => {
  let currentPaymentOption = null;
  if (paymentMethod.card) {
    currentPaymentOption = 'card';
  } else if (_.get(paymentMethod, 'stripeInvoice.invoiceRequested')) {
    currentPaymentOption = 'stripeInvoice';
  } else if (_.get(paymentMethod, 'xeroInvoice.invoiceRequested')) {
    currentPaymentOption = 'xeroInvoice';
  }
  return currentPaymentOption;
};

class SummaryPage extends Component {
  status = {
    active: 'Active',
    canceled: 'Canceled',
    incomplete: 'Incomplete',
    incomplete_expired: 'Expired',
    past_due: 'Payment Overdue',
    trialing: 'Trial Period',
    unpaid: 'Unpaid',
  }

  render() {
    const {
      plans, info, plan, expiryManagementPlans, subscription, card, stripeInvoice, xeroInvoice, actions, expiryManagementAddOn,
    } = this.props;

    const paymentInfo = (stripeInvoice || xeroInvoice) ? <p>Payment by {stripeInvoice ? 'Stripe' : 'Xero'} invoice currently enabled.</p> : <p>Credit Card Ending {card.last4} Expires {card.expiryMonth}/{card.expiryYear}</p>;

    let expiryCost = 0;
    if (plan.billingPeriod && expiryManagementAddOn && expiryManagementPlans) {
      const expiryTiers = expiryManagementPlans[plan.billingPeriod].tiers;
      const matchingTier = _.minBy(_.filter(expiryTiers, item => item.size >= plan.planSize), 'size');
      if (matchingTier) expiryCost = matchingTier.price;
    }
    const totalPrice = expiryCost + plan.price;
    const price = plan.billingPeriod ? `${formatPrice(totalPrice)} per ${{ monthly: 'month', yearly: 'year' }[plan.billingPeriod]}` : '';
    const currentPaymentOption = getCurrentPaymentOption({ card, stripeInvoice, xeroInvoice });
    return (
      <div>
        <h2>Organisation Information</h2>

        <EditToggle
          FormComponent={BusinessInfoForm}
          info={info}
          postAction={actions.submitInfo}
          editText={'Edit Details'}
        >
          <DataTable data={[
            { label: 'Name', data: info.name },
            { label: 'ABN', data: info.abn },
            { label: 'Email', data: info.email },
            { label: 'Phone', data: info.phone },
            { label: 'Address', data: (<Address address={info.address}/>) },
          ]}
          />
        </EditToggle>

        <Divider/>

        <h2>Subscription Plan</h2>

        <EditToggle
          FormComponent={SubscriptionForm}
          plans={plans}
          expiryManagementPlans={expiryManagementPlans}
          current={plan}
          postAction={actions.submitPlan}
          editText={'Change Plan'}
        >
          <DataTable data={[
            { label: 'Billing Period', data: _.upperFirst(plan.billingPeriod) },
            { label: 'Plan Size', data: plan.planSize },
            { label: 'Cost', data: price },
            { label: 'Charged', data: chargedOn(plan) },
            { label: 'Subscription Status', data: this.status[_.get(subscription, 'subscriptionStatus')] || '' },
          ]}
          />
        </EditToggle>
        <Divider/>

        <h2>Payment</h2>

        <EditToggle
          FormComponent={CardForm}
          postAction={actions.submitPayment}
          editText={'Change Payment'}
          email={info.email}
          currentPaymentOption={currentPaymentOption}
          coupon={false}
        >
          {paymentInfo}
        </EditToggle>
      </div>
    );
  }
}

@withCurrentUser
@connectForm(() => ({}))
class ActivatePage extends Component {
  constructor() {
    super();
    this.state = {
      loading: false,
      saveInProgress: false,
      errors: {},
      globalErrors: [],
    };
  }

  onSubmit = e => {
    e.preventDefault();
    const { stripe, elements, actions } = this.props;
    if (!stripe || !elements) {
      return;
    }
    const cardElement = elements.getElement(CardElement);

    this.props.form.validateFields(async (err, values) => {
      if (err) {
        message.error('Please address the validation issues below');
        return;
      }
      const submitData = { ...values };
      this.setState({
        saveInProgress: true,
      });
      const cardProvided = !_.get(values, 'cardInfo.empty', true);
      if (cardProvided) {
        const { token, error } = await this.props.stripe.createToken(cardElement);
        if (error) {
          this.setState({
            saveInProgress: false,
            errors: error ? { card: [error.message] } : {},
            globalErrors: error ? { card: [error.message] } : {},
          });
          return;
        }
        submitData.paymentMethod = {
          card: {
            token: token.id,
          },
        };
      }
      const { success, data } = await actions.submitAllData(submitData);
      if (!success) {
        this.setState({
          errors: data && data._errors || {},
          saveInProgress: false,
          globalErrors: data && (data._errors || {})._request || [],
        });
      }
    }).catch(err => {
      console.error(err);
      this.setState({
        saveInProgress: false,
      });
    });
  }

  render() {
    const {
      form, info, plans, expiryManagementPlans, plan,
    } = this.props;
    const activationMessage = <h2>Let's activate your subscription</h2>;

    return (
      <Fragment>
        {activationMessage}
        <Form onSubmit={this.onSubmit}>
          <ErrorsList errors={this.state.globalErrors}/>
          <BusinessInfoForm form={form} info={info} editMode={false}/>
          <SubscriptionForm form={form} plans={plans} expiryManagementPlans={expiryManagementPlans} current={plan} editMode={false}/>
          <CardForm form={form} editMode={false} showCoupon={true} coupon={info.coupon}/>
          <ButtonBar>
            <Button >
                Cancel
            </Button>
            <Button
              loading={this.state.saveInProgress}
              htmlType='submit'
              type='primary'
            >
              {this.props.submitText || 'Save and complete'}
            </Button>
          </ButtonBar>
        </Form>

      </Fragment>
    );
  }
}

export {
  BillingPage,
};
